import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'
import type {
  PackagingAuditKind,
  PackagingFieldCrossCheckStoredEntry,
  PackagingHeavyMetalsAuditStoredEntry,
  PackagingSvhcAuditStoredEntry,
} from '@/shared/hooks/api/packagingSuppliers'
import { useManualOverridePackagingItemAudit } from '@/shared/hooks/api/packagingSuppliers'
import {
  countWords,
  PACKAGING_HEAVY_METALS_AUDIT_VERDICT_OPTIONS,
  PACKAGING_SVHC_AUDIT_VERDICT_OPTIONS,
  SUBMISSION_AUDIT_RESULT_OPTIONS,
  validateManualOverrideForm,
} from './packagingAuditOverrideValidation'
import {
  packagingHeavyMetalsAuditVerdictLabelKey,
  packagingItemHeavyMetalsAuditStatusLabelKey,
} from './packagingItemHeavyMetalsAuditFilter'
import {
  packagingItemSvhcAuditStatusLabelKey,
  packagingSvhcAuditVerdictLabelKey,
} from './packagingItemSvhcAuditFilter'
import { heavyMetalsAuditVerdictToOverviewStatus } from './packagingItemHeavyMetalsAuditOverviewClient'
import { svhcAuditVerdictToOverviewStatus } from './packagingItemSvhcAuditOverviewClient'

type PackagingItemManualOverrideSheetProps = {
  supplierId: string
  itemId: string
  auditKind: PackagingAuditKind
  open: boolean
  onOpenChange: (open: boolean) => void
  fieldCrossCheckHistory?: PackagingFieldCrossCheckStoredEntry[]
  heavyMetalsAuditHistory?: PackagingHeavyMetalsAuditStoredEntry[]
  svhcAuditHistory?: PackagingSvhcAuditStoredEntry[]
}

function submissionCurrentResult(
  entry: PackagingFieldCrossCheckStoredEntry | undefined
): string | null {
  if (entry == null) {
    return null
  }
  if (entry.flagged) {
    return 'Flagged'
  }
  return 'Clear'
}

function resolveCurrentResult(
  auditKind: PackagingAuditKind,
  fieldCrossCheckHistory: PackagingFieldCrossCheckStoredEntry[],
  heavyMetalsAuditHistory: PackagingHeavyMetalsAuditStoredEntry[],
  svhcAuditHistory: PackagingSvhcAuditStoredEntry[]
): string | null {
  if (auditKind === 'submission') {
    return submissionCurrentResult(fieldCrossCheckHistory[0])
  }
  if (auditKind === 'heavy_metals') {
    return heavyMetalsAuditHistory[0]?.verdict ?? null
  }
  return svhcAuditHistory[0]?.verdict ?? null
}

function resolveResultOptions(auditKind: PackagingAuditKind): string[] {
  if (auditKind === 'submission') {
    return [...SUBMISSION_AUDIT_RESULT_OPTIONS]
  }
  if (auditKind === 'heavy_metals') {
    return [...PACKAGING_HEAVY_METALS_AUDIT_VERDICT_OPTIONS]
  }
  return [...PACKAGING_SVHC_AUDIT_VERDICT_OPTIONS]
}

function CurrentResultBadge({
  auditKind,
  currentResult,
}: {
  auditKind: PackagingAuditKind
  currentResult: string
}) {
  const { t } = useTranslation('packagingSuppliers')

  if (auditKind === 'submission') {
    const variant = currentResult === 'Flagged' ? 'destructive' : 'secondary'
    const labelKey =
      currentResult === 'Flagged'
        ? 'fieldCrossCheck.flagged'
        : 'fieldCrossCheck.clear'
    return <Badge variant={variant}>{t(labelKey)}</Badge>
  }

  if (auditKind === 'heavy_metals') {
    const status = heavyMetalsAuditVerdictToOverviewStatus(
      currentResult as PackagingHeavyMetalsAuditStoredEntry['verdict']
    )
    return (
      <Badge variant='secondary'>
        {t(packagingItemHeavyMetalsAuditStatusLabelKey(status))}
      </Badge>
    )
  }

  const status = svhcAuditVerdictToOverviewStatus(
    currentResult as PackagingSvhcAuditStoredEntry['verdict']
  )
  return (
    <Badge variant='secondary'>
      {t(packagingItemSvhcAuditStatusLabelKey(status))}
    </Badge>
  )
}

function NewResultLabel({
  auditKind,
  value,
}: {
  auditKind: PackagingAuditKind
  value: string
}) {
  const { t } = useTranslation('packagingSuppliers')

  if (auditKind === 'submission') {
    const labelKey =
      value === 'Flagged' ? 'fieldCrossCheck.flagged' : 'fieldCrossCheck.clear'
    return t(labelKey)
  }
  if (auditKind === 'heavy_metals') {
    return t(
      packagingHeavyMetalsAuditVerdictLabelKey(
        value as PackagingHeavyMetalsAuditStoredEntry['verdict']
      )
    )
  }
  return t(
    packagingSvhcAuditVerdictLabelKey(value as PackagingSvhcAuditStoredEntry['verdict'])
  )
}

export function PackagingItemManualOverrideSheet({
  supplierId,
  itemId,
  auditKind,
  open,
  onOpenChange,
  fieldCrossCheckHistory = [],
  heavyMetalsAuditHistory = [],
  svhcAuditHistory = [],
}: PackagingItemManualOverrideSheetProps) {
  const { t } = useTranslation('packagingSuppliers')
  const overrideMutation = useManualOverridePackagingItemAudit(
    supplierId,
    itemId
  )
  const [newResult, setNewResult] = useState('')
  const [reason, setReason] = useState('')

  const currentResult = useMemo(
    () =>
      resolveCurrentResult(
        auditKind,
        fieldCrossCheckHistory,
        heavyMetalsAuditHistory,
        svhcAuditHistory
      ),
    [
      auditKind,
      fieldCrossCheckHistory,
      heavyMetalsAuditHistory,
      svhcAuditHistory,
    ]
  )

  const resultOptions = useMemo(
    () => resolveResultOptions(auditKind),
    [auditKind]
  )

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen === open) {
      return
    }
    if (!nextOpen) {
      setNewResult('')
      setReason('')
      overrideMutation.reset()
    }
    onOpenChange(nextOpen)
  }

  const validation = validateManualOverrideForm({
    currentResult,
    newResult,
    reason,
    messages: {
      reasonRequired: t('itemDetail.manualOverride.reasonRequired'),
      reasonTooLong: t('itemDetail.manualOverride.reasonTooLong'),
      differentResultRequired: t(
        'itemDetail.manualOverride.differentResultRequired'
      ),
    },
  })

  const wordCount = countWords(reason)

  let sheetTitle = t('itemDetail.manualOverride.sheetTitleSubmission')
  if (auditKind === 'heavy_metals') {
    sheetTitle = t('itemDetail.manualOverride.sheetTitleHeavyMetals')
  } else if (auditKind === 'svhc') {
    sheetTitle = t('itemDetail.manualOverride.sheetTitleSvhc')
  }

  const handleSave = async () => {
    if (!validation.canSave) {
      return
    }
    try {
      await overrideMutation.mutateAsync({
        auditKind,
        newResult,
        reason: reason.trim(),
      })
      handleOpenChange(false)
    } catch {
      // Error state handled below.
    }
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className='flex flex-col sm:max-w-lg'>
        <SheetHeader>
          <SheetTitle>{sheetTitle}</SheetTitle>
          <SheetDescription>
            {t('itemDetail.manualOverride.sheetDescription')}
          </SheetDescription>
        </SheetHeader>

        <div className='min-h-0 flex-1 space-y-6 overflow-y-auto py-4'>
          {currentResult != null ? (
            <div className='space-y-2'>
              <Label>{t('itemDetail.manualOverride.currentResult')}</Label>
              <div>
                <CurrentResultBadge
                  auditKind={auditKind}
                  currentResult={currentResult}
                />
              </div>
            </div>
          ) : null}

          <div className='space-y-2'>
            <Label htmlFor='manual-override-new-result'>
              {t('itemDetail.manualOverride.newResult')}
            </Label>
            <Select
              value={newResult}
              onValueChange={(value) => {
                setNewResult(value ?? '')
              }}
            >
              <SelectTrigger id='manual-override-new-result' className='w-full'>
                <SelectValue
                  placeholder={t('itemDetail.manualOverride.newResultPlaceholder')}
                >
                  {newResult !== '' ? (
                    <NewResultLabel auditKind={auditKind} value={newResult} />
                  ) : null}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {resultOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    <NewResultLabel auditKind={auditKind} value={option} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {validation.resultError != null ? (
              <p className='text-sm text-destructive'>{validation.resultError}</p>
            ) : null}
          </div>

          <div className='space-y-2'>
            <Label htmlFor='manual-override-reason'>
              {t('itemDetail.manualOverride.reasonLabel')}
            </Label>
            <Textarea
              id='manual-override-reason'
              value={reason}
              onChange={(event) => {
                setReason(event.target.value)
              }}
              rows={6}
            />
            <p className='text-sm text-muted-foreground'>
              {t('itemDetail.manualOverride.reasonHelper', { count: wordCount })}
            </p>
            {validation.reasonError != null ? (
              <p className='text-sm text-destructive'>{validation.reasonError}</p>
            ) : null}
          </div>

          {overrideMutation.isError ? (
            <p className='text-sm text-destructive'>
              {t('itemDetail.manualOverride.saveFailed')}
            </p>
          ) : null}
        </div>

        <SheetFooter className='gap-2 sm:justify-end'>
          <Button
            type='button'
            variant='outline'
            onClick={() => {
              handleOpenChange(false)
            }}
          >
            {t('itemDetail.manualOverride.cancel')}
          </Button>
          <Button
            type='button'
            disabled={!validation.canSave || overrideMutation.isPending}
            onClick={() => {
              void handleSave()
            }}
          >
            {overrideMutation.isPending
              ? t('itemDetail.manualOverride.saving')
              : t('itemDetail.manualOverride.save')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
